
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import draggable from "vuedraggable";
import {
  JielongIntroCardDetail,
  CommonTag,
  createGoodsTag,
  deleteGoodsTag,
  fetchCommonTags,
} from "@/api/jielong";
import { sftpChooseFile } from "@/utils/h5ToApp";
// import { downloadUrl } from "@/utils/util";
// import TagSelector from "./tagSelector.vue";
import TagSelector from "@/components/TagSelectPopup/index.vue";

@Component({
  components: { draggable, TagSelector },
})
export default class JielongIntroCard extends Vue {
  @Prop({ type: Object, required: true }) value!: JielongIntroCardDetail;
  @Prop({ type: Number, default: 0 }) index!: number;
  @Prop({ type: Number, default: 1 }) total!: number;
  @Prop({ type: Array, default: () => [] }) commonTags!: CommonTag[];
  @Prop({ type: Number, default: 9 }) maxSmallImages!: number;
  @Prop({ type: Number, default: 5 }) maxTags!: number;
  @Prop({ type: Boolean, default: false }) readonly!: boolean;

  @Getter companyId!: string;

  // downloadUrl = downloadUrl;

  sftpUrl = "";

  // 小图本地副本（不能直接修改 prop）。_key 仅用于渲染稳定 key，不参与持久化
  localSmallImages: Array<{ url: string; _key: string }> = [];
  private smallKeySeed = 0;

  // 标签弹窗
  tagSelectorVisible = false;

  get isFirst (): boolean {
    return this.index === 0;
  }

  get isLast (): boolean {
    return this.index === this.total - 1;
  }

  get typeLabel (): string {
    const map: Record<string, string> = {
      bigImage: "大图",
      smallImage: "小图",
      video: "视频",
      text: "文字",
      aiText: "文字",
      tag: "标签",
    };
    return map[this.value.type] || "";
  }

  // 静态 require 映射，避免动态路径因文件名不匹配（bigImage.png vs big-image.png）导致 webpack 报错
  get typeIconSrc (): string {
    const map: Record<string, string> = {
      bigImage:   "big-image.png",
      smallImage: "small-image.png",
      video:      "video.png",
      text:       "text.png",
      aiText:     "text.png",
      tag:        "tag.png",
    };
    return  require(`@/assets/images/jielong/${map[this.value.type]}`) || "";
  }

  // 父组件通过 $set 替换卡片对象（引用变化），无需 deep watch
  @Watch("value", { immediate: true })
  onValueChange (): void {
    if (!this.value) return;
    if (this.value.type === "text" || this.value.type === "aiText") {
      this.$nextTick(() => this.resizeTextarea());
      return;
    }
    if (this.value.type !== "smallImage") return;
    const incoming = (this.value.value as string[]) || [];
    // 仅当文件序列真正变化时才重建本地副本，避免 emit 回环导致 key 重新生成、图片闪动
    const samePaths =
      incoming.length === this.localSmallImages.length &&
      incoming.every((it, i) => it === this.localSmallImages[i].url);
    if (!samePaths) {
      this.localSmallImages = incoming.map(url => ({
        url,
        _key: this.genSmallKey(),
      }));
    }
  }

  mounted (): void {
    this.sftpUrl = this.$store.state.user.sftpUrl
    if (this.value && (this.value.type === "text" || this.value.type === "aiText")) {
      this.$nextTick(() => this.resizeTextarea());
    }
  }

  private genSmallKey (): string {
    this.smallKeySeed += 1;
    return "s" + Date.now() + "-" + this.smallKeySeed;
  }

  // 去掉本地渲染用的 _key，得到可持久化的小图 URL 数组
  private stripSmallKeys (): string[] {
    return this.localSmallImages.map(it => it.url);
  }

  // ===== 操作按钮 =====
  onMoveUp (): void {
    if (this.readonly) return;
    if (this.isFirst) return;
    this.$emit("move-up");
  }

  onMoveDown (): void {
    if (this.readonly) return;
    if (this.isLast) return;
    this.$emit("move-down");
  }

  onMoveTop (): void {
    if (this.readonly) return;
    if (this.isFirst) return;
    this.$emit("move-top");
  }

  onDelete (): void {
    if (this.readonly) return;
    this.$emit("delete");
  }

  // ===== 通用：发出新卡片对象 =====
  private emitInput (newValue: JielongIntroCardDetail["value"]): void {
    this.$emit("input", { ...this.value, value: newValue });
  }

  // chooseFile 结果归一化为数组
  private normalizeFiles (res: any): any[] {
    return Array.isArray(res) ? res : [];
  }

  // ===== 大图 =====
  chooseBigImage (): void {
    if (this.readonly) return;
    const data = { fileType: "1", maxFileCount: "1" };
    sftpChooseFile(data).then((res: any) => {
      const list = this.normalizeFiles(res);
      if (!list.length) return;
      this.emitInput(list[0].filePath as string);
    });
  }

  // ===== 小图 =====
  chooseSmallImages (): void {
    if (this.readonly) return;
    const remain = this.maxSmallImages - this.localSmallImages.length;
    if (remain <= 0) return;
    const data = { fileType: "1", maxFileCount: String(remain) };
    sftpChooseFile(data).then((res: any) => {
      const list = this.normalizeFiles(res);
      if (!list.length) return;
      const appended = list.map((item: any) => ({
        url: item.filePath as string,
        _key: this.genSmallKey(),
      }));
      this.localSmallImages = this.localSmallImages
        .concat(appended)
        .slice(0, this.maxSmallImages);
      this.emitInput(this.stripSmallKeys());
    });
  }

  removeSmallImage (idx: number): void {
    if (this.readonly) return;
    this.localSmallImages.splice(idx, 1);
    this.emitInput(this.stripSmallKeys());
  }

  onSmallSortEnd (): void {
    if (this.readonly) return;
    this.emitInput(this.stripSmallKeys());
  }

  // ===== 视频 =====
  chooseVideo (): void {
    if (this.readonly) return;
    const data = { fileType: "2", maxFileCount: "1" };
    sftpChooseFile(data).then((res: any) => {
      const list = this.normalizeFiles(res);
      if (!list.length) return;
      this.emitInput(list[0].filePath as string);
    });
  }

  removeVideo (): void {
    if (this.readonly) return;
    this.emitInput(null);
  }

  // ===== 文字 =====
  onTextInput (e: Event): void {
    if (this.readonly) return;
    const target = e.target as HTMLTextAreaElement;
    this.resizeTextarea(target);
    this.emitInput(target.value);
  }

  /** 供父组件在新增文字卡片后聚焦输入 */
  focusTextarea (): void {
    if (this.readonly) return;
    if (this.value.type !== "text" && this.value.type !== "aiText") return;
    const target = this.$refs.textarea as HTMLTextAreaElement | undefined;
    if (!target) return;
    target.focus();
    const len = target.value ? target.value.length : 0;
    try {
      target.setSelectionRange(len, len);
    } catch (e) {
      /* 部分环境对 setSelectionRange 支持不全 */
    }
  }

  // 文字域高度自适应（输入时与初始渲染回填长文本时都需要）
  private resizeTextarea (el?: HTMLTextAreaElement): void {
    const target = el || (this.$refs.textarea as HTMLTextAreaElement);
    if (!target) return;
    target.style.height = "auto";
    target.style.height = target.scrollHeight + "px";
  }

  // ===== 标签 =====
  // commonTags(CommonTag[]) → TagSelectPopup 期望的两条 string[] 分别是系统标签和自定义标签
  get systemTagLabels (): string[] {
    return (this.commonTags || []).filter(t => !t.custom).map(t => t.label);
  }

  get customTagLabels (): string[] {
    return (this.commonTags || []).filter(t => t.custom).map(t => t.label);
  }

  // 传给 TagSelectPopup 的接口函数，调用 API 后 emit 通知父组件同步 commonTags
  async doAddTag (label: string): Promise<boolean> {
    try {
      await createGoodsTag({ storeId: this.companyId, tagName: label });
      const tags = await fetchCommonTags(this.companyId);
      this.$emit("add-common-tag", tags);
      return true;
    } catch {
      return false;
    }
  }

  async doDeleteTag (label: string): Promise<boolean> {
    try {
      const found = (this.commonTags || []).find(t => t.label === label);
      if (!found || !found.tagId) return false;
      await deleteGoodsTag({ storeId: this.companyId, tagId: found.tagId });
      this.$emit("delete-common-tag", label);
      return true;
    } catch {
      return false;
    }
  }

  openTagSelector (): void {
    if (this.readonly) return;
    this.tagSelectorVisible = true;
  }

  onTagConfirm (tags: string[]): void {
    if (this.readonly) return;
    this.emitInput(tags);
  }

  // 直接在卡片上删除已选标签
  removeTag (idx: number): void {
    if (this.readonly) return;
    const tags = ((this.value.value as string[]) || []).slice();
    tags.splice(idx, 1);
    this.emitInput(tags);
  }
}
